import React, { useEffect, useState } from 'react';
import PartnerLayout from '../../components/layout/PartnerLayout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Users,
  FileText,
  ListFilter,
  DollarSign,
  UserPlus,
  ArrowUpRight,
  Briefcase,
  CheckCircle2,
  Clock,
  Plus,
  MessageSquare,
  Sparkles,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { partnerAPI } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import PartnerApplyServiceFlow, {
  PARTNER_SERVICE_CATALOG,
} from '../../components/partner/PartnerApplyServiceFlow';
import profileAssessmentIcon from '../../assets/Icon/Profile Assessment.png';
import preapplicationSupportIcon from '../../assets/Icon/Preapplication Support.png';
import applyUniversityIcon from '../../assets/Icon/Apply University.png';
import visaInterviewIcon from '../../assets/Icon/Visa & Interview Support.png';
import ticketTravelIcon from '../../assets/Icon/Ticket & Travel Support.png';
import accommodationIcon from '../../assets/Icon/Find Accommodation.png';
import educationLoanIcon from '../../assets/Icon/Education Loan.png';
import jobsAbroadIcon from '../../assets/Icon/Find Jobs Abroad.png';
import serviceImage1 from '../../assets/services-img/services-1.png';
import serviceImage2 from '../../assets/services-img/services-2.png';
import serviceImage3 from '../../assets/services-img/services-3.png';
import serviceImage4 from '../../assets/services-img/services-4.png';
import serviceImage5 from '../../assets/services-img/services-5.png';
import serviceImage6 from '../../assets/services-img/services-6.png';
import serviceImage7 from '../../assets/services-img/services-7.jpg';
import serviceImage8 from '../../assets/services-img/services-8.png';
import welcome from '../../assets/background-welcome.png';
import logo from '../../assets/pictures/logo.png';

export default function PartnerDashboardMain() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [dashboard, setDashboard] = useState({
    totalStudents: 0,
    activeStudents: 0,
    totalServiceRequests: 0,
    activeServiceRequests: 0,
    completedServiceRequests: 0,
    totalTasks: 0,
    pendingTasks: 0,
    totalEarnings: 0,
    pendingEarnings: 0,
  });
  const [recentRequests, setRecentRequests] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const serviceVisuals = {
    PROFILE_ASSESSMENT: {
      iconAsset: profileAssessmentIcon,
      imageAsset: serviceImage1,
      shell: 'from-[#efe3ff] via-[#e3d2ff] to-[#d2b7ff]',
      glow: 'shadow-[0_10px_30px_rgba(168,125,250,0.25)]',
      border: 'border-[#9b6ffc]/55',
      tint: 'from-[#a87dfa]/48 via-[#a87dfa]/18 to-transparent',
      title: 'text-[#6d49d4]',
      chip: 'text-[#7a55df]',
      iconWrap: 'border-[#a87dfa]/40 bg-[#f3e9ff]',
      iconColor: 'text-[#7a55df]',
    },
    UNIVERSITY_SHORTLISTING: {
      iconAsset: preapplicationSupportIcon,
      imageAsset: serviceImage2,
      shell: 'from-[#e4f0ff] via-[#d2e6ff] to-[#bddbff]',
      glow: 'shadow-[0_10px_30px_rgba(111,168,255,0.24)]',
      border: 'border-[#6fa8ff]/55',
      tint: 'from-[#6fa8ff]/48 via-[#6fa8ff]/18 to-transparent',
      title: 'text-[#3f7edc]',
      chip: 'text-[#4a8cf1]',
      iconWrap: 'border-[#6fa8ff]/40 bg-[#edf5ff]',
      iconColor: 'text-[#4a8cf1]',
    },
    APPLICATION_ASSISTANCE: {
      iconAsset: applyUniversityIcon,
      imageAsset: serviceImage3,
      shell: 'from-[#ffe3ee] via-[#ffd2e5] to-[#ffc0da]',
      glow: 'shadow-[0_10px_30px_rgba(255,155,194,0.24)]',
      border: 'border-[#ff9bc2]/55',
      tint: 'from-[#ff9bc2]/46 via-[#ff9bc2]/18 to-transparent',
      title: 'text-[#d45f93]',
      chip: 'text-[#dd6f9f]',
      iconWrap: 'border-[#ff9bc2]/40 bg-[#fff0f7]',
      iconColor: 'text-[#dd6f9f]',
    },
    VISA_GUIDANCE: {
      iconAsset: visaInterviewIcon,
      imageAsset: serviceImage4,
      shell: 'from-[#dffaf5] via-[#cdf4eb] to-[#b4eadf]',
      glow: 'shadow-[0_10px_30px_rgba(120,214,198,0.24)]',
      border: 'border-[#78d6c6]/55',
      tint: 'from-[#78d6c6]/46 via-[#78d6c6]/18 to-transparent',
      title: 'text-[#24998a]',
      chip: 'text-[#2aa898]',
      iconWrap: 'border-[#78d6c6]/40 bg-[#ecfffb]',
      iconColor: 'text-[#2aa898]',
    },
    SCHOLARSHIP_SEARCH: {
      iconAsset: ticketTravelIcon,
      imageAsset: serviceImage5,
      shell: 'from-[#fff0d8] via-[#ffe5be] to-[#ffd497]',
      glow: 'shadow-[0_10px_30px_rgba(255,191,102,0.24)]',
      border: 'border-[#ffbf66]/55',
      tint: 'from-[#ffbf66]/46 via-[#ffbf66]/18 to-transparent',
      title: 'text-[#d28a20]',
      chip: 'text-[#e59a28]',
      iconWrap: 'border-[#ffbf66]/40 bg-[#fff7ea]',
      iconColor: 'text-[#e59a28]',
    },
    ACCOMMODATION_HELP: {
      iconAsset: accommodationIcon,
      imageAsset: serviceImage6,
      shell: 'from-[#e2f7dc] via-[#d1efc8] to-[#b8e4ab]',
      glow: 'shadow-[0_10px_30px_rgba(143,209,127,0.24)]',
      border: 'border-[#8fd17f]/55',
      tint: 'from-[#8fd17f]/46 via-[#8fd17f]/18 to-transparent',
      title: 'text-[#4e9b40]',
      chip: 'text-[#5aa64a]',
      iconWrap: 'border-[#8fd17f]/40 bg-[#f1ffec]',
      iconColor: 'text-[#5aa64a]',
    },
    LOAN_ASSISTANCE: {
      iconAsset: educationLoanIcon,
      imageAsset: serviceImage7,
      shell: 'from-[#dff3ff] via-[#caeaff] to-[#addbff]',
      glow: 'shadow-[0_10px_30px_rgba(126,200,255,0.24)]',
      border: 'border-[#7ec8ff]/55',
      tint: 'from-[#7ec8ff]/46 via-[#57a8ff]/18 to-transparent',
      title: 'text-[#3b84c7]',
      chip: 'text-[#4794db]',
      iconWrap: 'border-[#7ec8ff]/40 bg-[#edf8ff]',
      iconColor: 'text-[#4794db]',
    },
    PRE_DEPARTURE_ORIENTATION: {
      iconAsset: jobsAbroadIcon,
      imageAsset: serviceImage8,
      shell: 'from-[#ffe4dd] via-[#ffd3c8] to-[#ffbdac]',
      glow: 'shadow-[0_10px_30px_rgba(255,159,139,0.24)]',
      border: 'border-[#ff9f8b]/55',
      tint: 'from-[#ff9f8b]/46 via-[#ff9f8b]/18 to-transparent',
      title: 'text-[#d36852]',
      chip: 'text-[#e17a65]',
      iconWrap: 'border-[#ff9f8b]/40 bg-[#fff1ed]',
      iconColor: 'text-[#e17a65]',
    },
  };

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [dashRes, reqRes] = await Promise.all([
        partnerAPI.getDashboard().catch(() => ({})),
        partnerAPI
          .getServiceRequests({ page: 1, limit: 5 })
          .catch(() => ({ data: [] })),
      ]);

      if (dashRes.dashboard) setDashboard(dashRes.dashboard);
      const reqs = reqRes.data || [];
      setRecentRequests(reqs);

      // Derive unique counselors from recent requests
      const counselorMap = {};
      reqs.forEach(r => {
        const c = r.counselor || r.assignedCounselor;
        if (c) {
          const key = c._id || c.userId || c.email;
          if (!counselorMap[key]) counselorMap[key] = { ...c, count: 0 };
          counselorMap[key].count += 1;
        }
      });
      setCounselors(Object.values(counselorMap));
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Students',
      value: dashboard.totalStudents,
      subtitle: `${dashboard.activeStudents} active`,
      icon: Users,
      action: () => navigate('/partner/students'),
    },
    {
      title: 'Service Requests',
      value: dashboard.totalServiceRequests,
      subtitle: `${dashboard.activeServiceRequests} in progress · ${dashboard.completedServiceRequests} done`,
      icon: FileText,
      action: () => navigate('/partner/service-requests'),
    },
    {
      title: 'Pending Tasks',
      value: dashboard.pendingTasks,
      subtitle: `${dashboard.totalTasks} total`,
      icon: ListFilter,
      action: () => navigate('/partner/tasks'),
    },
    {
      title: 'Earnings',
      value: `$${(dashboard.totalEarnings || 0).toLocaleString()}`,
      subtitle: `$${(dashboard.pendingEarnings || 0).toLocaleString()} pending`,
      icon: DollarSign,
      action: () => navigate('/partner/commissions'),
    },
  ];

  if (loading) {
    return (
      <PartnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </PartnerLayout>
    );
  }

  return (
    <PartnerLayout>
      <div className="space-y-6">
        {/* Welcome */}
        <div className="partner-card partner-card-hover relative overflow-hidden rounded-[30px] border border-[#A87DFA]/45 bg-gradient-to-br from-[#efe3ff] via-[#e3d2ff] to-[#d2b7ff] p-4 shadow-[0_10px_30px_rgba(168,125,250,0.25)] lg:p-5">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(168,125,250,0.18),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(168,125,250,0.12),transparent_30%)]" />
          <div className="relative grid gap-5 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div className="relative overflow-hidden rounded-[24px] bg-white/18 p-6 backdrop-blur-sm">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/40 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-[#8D68E8]">
                <img
                  src={logo}
                  alt="Fly8"
                  className="h-4 w-auto object-contain"
                />
                <Sparkles className="h-3.5 w-3.5" />
                Partner Workspace
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-[#6d49d4] lg:text-[2.1rem]">
                Welcome back, {user?.firstName}!
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-700">
                Manage your students and service requests as a Fly8 Partner.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => navigate('/partner/add-student')}
                  variant="outline"
                  className="rounded-2xl border-[#a87dfa]/45 bg-[#f7efff] text-[#6d49d4] hover:bg-[#efe0ff]"
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add Student
                </Button>
                <Button
                  onClick={() => setApplyOpen(true)}
                  className="rounded-2xl border border-[#a87dfa]/45 bg-[#f7efff] text-[#6d49d4] hover:bg-[#efe0ff]"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Request
                </Button>
              </div>
            </div>
            <div className="relative h-full min-h-[200px]rounded-[24px]">
              <img
                src={welcome}
                alt="Partner Dashboard"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0" />
              <div className="absolute bottom-4 left-4 rounded-full border border-white/40 bg-white/55 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7a55df] backdrop-blur-md">
                Partner Workspace
              </div>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
              >
                <Card
                  className="partner-card partner-card-hover cursor-pointer rounded-[24px] p-5"
                  onClick={stat.action}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">
                        {stat.title}
                      </p>
                      <p className="mt-2 text-3xl font-bold text-slate-900">
                        {stat.value}
                      </p>
                      <p className="mt-2 text-xs text-slate-500">
                        {stat.subtitle}
                      </p>
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#A87DFA]/18 bg-[linear-gradient(135deg,rgba(168,125,250,0.18),rgba(168,125,250,0.08))] shadow-[0_14px_28px_rgba(168,125,250,0.14)]">
                      <Icon className="h-5 w-5 text-[#7A55DF]" />
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Requests */}
          <Card className="partner-card rounded-[28px] p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Recent Service Requests
              </h3>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-2xl text-[#6D49D4] hover:bg-[#A87DFA]/10"
                onClick={() => navigate('/partner/service-requests')}
              >
                View all <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            {recentRequests.length === 0 ? (
              <div className="text-center py-8">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#A87DFA]/16 bg-white/45">
                  <FileText className="h-6 w-6 text-[#A87DFA]" />
                </div>
                <p className="text-sm text-slate-600">No requests yet</p>
                <Button
                  size="sm"
                  className="partner-primary-btn mt-3 rounded-2xl"
                  onClick={() => setApplyOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-1" /> Create Your First Request
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {recentRequests.map(r => {
                  const def =
                    PARTNER_SERVICE_CATALOG.find(
                      s => s.serviceType === r.serviceType,
                    ) || {};
                  const Icon = def.icon || FileText;
                  const isCompleted = r.status === 'COMPLETED';
                  return (
                    <button
                      key={r.serviceRequestId || r._id}
                      onClick={() =>
                        navigate(
                          `/partner/service-requests/${r.serviceRequestId || r._id}`,
                        )
                      }
                      className="partner-card-hover flex w-full items-center gap-3 rounded-[22px] border border-[#A87DFA]/12 bg-white/35 p-3 text-left"
                    >
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#A87DFA]/16 bg-[linear-gradient(135deg,rgba(168,125,250,0.18),rgba(168,125,250,0.06))]">
                        <Icon className="h-4 w-4 text-[#7A55DF]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {def.label || r.serviceType?.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-slate-500">
                          {r.student?.user
                            ? `${r.student.user.firstName} ${r.student.user.lastName}`
                            : 'Student'}
                          {' · '}
                          {r.updatedAt &&
                            formatDistanceToNow(new Date(r.updatedAt), {
                              addSuffix: true,
                            })}
                        </p>
                      </div>
                      <Badge
                        className={
                          isCompleted
                            ? 'border-[#A87DFA]/20 bg-[#A87DFA]/12 text-[#6D49D4]'
                            : 'border-[#A87DFA]/20 bg-[#A87DFA]/12 text-[#6D49D4]'
                        }
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                        ) : (
                          <Clock className="w-3 h-3 mr-1" />
                        )}
                        {r.status?.replace(/_/g, ' ')}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Counselors */}
          <Card className="partner-card rounded-[28px] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Your Counselors
              </h3>
            </div>
            {counselors.length === 0 ? (
              <div className="text-center py-6">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-[#A87DFA]/16 bg-white/45">
                  <MessageSquare className="h-5 w-5 text-[#A87DFA]" />
                </div>
                <p className="text-xs text-slate-500">
                  Counselors will appear here once assigned to your requests
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {counselors.map((c, i) => (
                  <div
                    key={c._id || c.userId || i}
                    className="flex items-center gap-3 rounded-[22px] border border-[#A87DFA]/12 bg-white/30 p-3"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[linear-gradient(135deg,#A87DFA,#8E66F1)] text-sm font-semibold text-white shadow-[0_12px_30px_rgba(168,125,250,0.22)]">
                      {c.firstName?.[0]}
                      {c.lastName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {c.firstName} {c.lastName}
                      </p>
                      <p className="text-xs text-slate-500">
                        {c.count} case{c.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="partner-secondary-btn mt-2 w-full rounded-2xl"
                  onClick={() => navigate('/partner/messages')}
                >
                  <MessageSquare className="w-3.5 h-3.5 mr-1" /> Message
                  Counselors
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Services Shortcut */}
        <Card className="partner-card rounded-[28px] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#8D68E8]" />
              <h3 className="text-lg font-semibold text-slate-900">
                Browse Services
              </h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-2xl text-[#6D49D4] hover:bg-[#A87DFA]/10"
              onClick={() => navigate('/partner/services')}
            >
              View all <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {PARTNER_SERVICE_CATALOG.slice(0, 4).map(s => {
              const Icon = s.icon;
              const visuals = serviceVisuals[s.serviceType] || {};
              return (
                <button
                  key={s.serviceType}
                  onClick={() => navigate('/partner/services')}
                  className={`partner-card partner-card-hover relative overflow-hidden rounded-[24px] border bg-gradient-to-br p-3 text-left ${visuals.shell || 'from-white/70 to-white/40'} ${visuals.border || 'border-[#A87DFA]/24'} ${visuals.glow || ''}`}
                >
                  <div
                    className={`pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-[24px] bg-gradient-to-b ${visuals.tint || 'from-[#A87DFA]/20 via-[#A87DFA]/8 to-transparent'}`}
                  />
                  <div className="relative mb-3 overflow-hidden rounded-[18px] border border-white/45">
                    <img
                      src={visuals.imageAsset}
                      alt={s.label}
                      className="h-24 w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.16))]" />
                    <div
                      className={`absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-xl border ${visuals.iconWrap || 'border-[#A87DFA]/16 bg-white/55'} backdrop-blur-md`}
                    >
                      {visuals.iconAsset ? (
                        <img
                          src={visuals.iconAsset}
                          alt=""
                          className="h-5 w-5 object-contain"
                        />
                      ) : (
                        <Icon
                          className={`h-4 w-4 ${visuals.iconColor || 'text-[#7A55DF]'}`}
                        />
                      )}
                    </div>
                    <div
                      className={`absolute bottom-3 left-3 rounded-full border border-white/40 bg-white/55 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] backdrop-blur-md ${visuals.chip || 'text-[#7A55DF]'}`}
                    >
                      Fly8 Service
                    </div>
                  </div>
                  <div className="mb-2 flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em]">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-xl border ${visuals.iconWrap || 'border-[#A87DFA]/16 bg-white/55'}`}
                    >
                      <Icon
                        className={`h-3.5 w-3.5 ${visuals.iconColor || 'text-[#7A55DF]'}`}
                      />
                    </div>
                    <span className={visuals.chip || 'text-[#7A55DF]'}>
                      Partner Service
                    </span>
                  </div>
                  <p
                    className={`truncate text-base font-semibold ${visuals.title || 'text-slate-900'}`}
                  >
                    {s.label}
                  </p>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      <PartnerApplyServiceFlow
        open={applyOpen}
        onOpenChange={setApplyOpen}
        onCreated={loadAll}
      />
    </PartnerLayout>
  );
}
